import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Button, Card, Col, List, Row, Tabs } from 'antd';
import { observer } from 'mobx-react';
import { TProduct } from 'types/product';
import { TProductCategory } from 'types/productCategory';

import { ActionsMenu } from '@atoms/ActionsMenu';
import { CardSkeleton } from '@atoms/skeletons/cardSkeleton';
import { SettingsManager } from '@classes/settingsManager';
import { SkeletonWrapper } from '@common/SkeletonWrapper/SkeletonWrapper';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFetchDataQuery } from '@hooks/useFetchDataQuery';
import { normalizeFilter, normalizeSort } from '@hooks/useFetchDataQuery/helpers';
import { useMobxStoreToState } from '@hooks/useMobxStoreToState';
import { usePageList } from '@hooks/usePageList';
import { useSearch } from '@hooks/useSearch';
import { filtersOldToNew, sortsOldToNew } from '@hooks/useSearch/transports';
import { ServiceProductList } from '@molecules/renderList/serviceProductList';
import { FilterStore } from '@organisms/productServicesFilter/filterStore';

type TProductList = (TProduct & { category: TProductCategory })[];

const filterStore = new FilterStore();

export const StorageList = observer(() => {
    const navigate = useNavigate();
    const { crmId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = useMobxStoreToState(filterStore.filter);
    const newFilters = useMemo(
        () => filtersOldToNew(filter),
        [filter.createdDates, filter.deleted, filter.profile, filter.updatedDates, filter.price]
    );
    const newSorts = useMemo(() => sortsOldToNew(filter), [filter.sortBy, filter.sortDirection]);
    const normalizedFilters = useMemo(
        () => normalizeFilter(filter),
        [filter.createdDates, filter.deleted, filter.profile, filter.updatedDates, filter.price]
    );
    const normalizedSorts = useMemo(() => {
        const { sortBy, sortDirection } = filter;
        const sort = { sortBy, sortDirection };
        return normalizeSort(sort);
    }, [filter.sortBy, filter.sortDirection]);
    const searchQuery = useMemo(() => filter.query, [filter.query]);

    const [itemsList, setItemsList] = useState<TProductList>([]);
    const creds = SettingsManager.getConnectionCredentials();
    const tabFromUrl = searchParams.get('tab') || '1';
    const [tabNumber, setTabNumber] = useState(tabFromUrl);

    const {
        queryMeta,
        pageSize,
        setQueryMeta,
        selectedItems,
        updateSelectedItems,
        forceReloadState,
    } = usePageList({
        batchModels: ['products', 'product_categories'],
    });

    const { isLoading, data: warehousesData } = useFetchDataQuery({
        getFnKey: 'getWarehousesList',
        params: { crm_id: +crmId },
    });

    const {
        isLoading: isItemsListLoading,
        isFetching: isItemsListFetching,
        isFetchingNextPage: isItemsFetchingNextPage,
        fetchNextPage: fetchItemsNextPage,
    } = useFetchDataQuery({
        getFnKey: 'getProductList',
        params: {
            crm_id: creds.crmID,
            category_id: '',
            filters: {
                ...normalizedFilters,
                deleted: tabNumber === '3' ? 'only' : 'null',
                is_other: tabNumber === '5' ? undefined : tabNumber === '4',
                is_draft: tabNumber === '5' ? 'only' : undefined,
                warehouse_id: undefined,
                price: normalizedFilters.price || null,
            } as any,
            ...normalizedSorts,
            entityType: ['product'] as any,
            is_permission: true,
        } as any,
        pageSize,
        setQueryMeta,
        setDataList: setItemsList,
        forceReloadState,
    });

    // полный (ре)фетч списка — например, после инвалидации кэша при создании
    // товара; подгрузка следующих страниц скелетоны не показывает
    const isItemsListBusy =
        isItemsListLoading || (isItemsListFetching && !isItemsFetchingNextPage);

    const { isSearchActive, searchResult, searchMore } = useSearch({
        model: 'product',
        query: searchQuery,
        filters: newFilters,
        sorts: newSorts,
    });

    const handleChangeTab = (key: string) => {
        setTabNumber(key);
        setSearchParams({
            ...Object.fromEntries(searchParams),
            tab: key,
            page: '1',
        });
        filterStore.changeDeleted(key === '3' ? 'only' : 'null');
        filterStore.changeQuery('');
    };

    const handleNavigateWarehouse = (e, storage: any) => {
        e.stopPropagation();
        navigate(`${storage.id}/edit`);
    };

    const doCreate = () => {
        navigate(`create`);
    };

    function openCard(id: number) {
        navigate(`/lk/worker/crm/${creds.crmID}/storages/1/products/${id}`);
    }

    return (
        <div id="app-product-list">
            <Tabs
                defaultActiveKey={tabFromUrl}
                destroyInactiveTabPane={true}
                items={[
                    {
                        label: 'Склады',
                        key: '1',
                        children: (
                            <SkeletonWrapper
                                isLoading={isLoading}
                                skeleton={[1, 2, 3].map((id) => (
                                    <CardSkeleton key={id} />
                                ))}
                            >
                                <List
                                    dataSource={warehousesData || []}
                                    renderItem={(wareehouse: any) => (
                                        <List.Item>
                                            <Card
                                                title={
                                                    <Row
                                                        justify="space-between"
                                                        align="middle"
                                                        gutter={16}
                                                    >
                                                        <Col>{wareehouse.name}</Col>
                                                        <Col>
                                                            <Button
                                                                shape="circle"
                                                                icon={
                                                                    <FontAwesomeIcon
                                                                        icon={faPencil}
                                                                    />
                                                                }
                                                                onClick={(e) =>
                                                                    handleNavigateWarehouse(
                                                                        e,
                                                                        wareehouse
                                                                    )
                                                                }
                                                                type={'primary'}
                                                            />
                                                        </Col>
                                                    </Row>
                                                }
                                                hoverable
                                                onClick={() =>
                                                    navigate(`${wareehouse.id}/products`)
                                                }
                                                style={{ width: '100%' }}
                                            >
                                                <p>{wareehouse.description || 'Нет описания'}</p>
                                            </Card>
                                        </List.Item>
                                    )}
                                />
                                <ActionsMenu doCreate={doCreate} />
                            </SkeletonWrapper>
                        ),
                    },

                    {
                        label: 'Корзина',
                        key: '3',
                        children: (
                            <SkeletonWrapper
                                isLoading={isItemsListBusy}
                                skeleton={[1, 2, 3].map((id) => (
                                    <CardSkeleton key={id} />
                                ))}
                            >
                                <ServiceProductList
                                    productCategoryList={null}
                                    serviceCategoryList={[]}
                                    productList={itemsList}
                                    serviceList={null}
                                    selectElemList={selectedItems.products}
                                    selectCatList={selectedItems.product_categories}
                                    currentCategoryPage={1}
                                    currentCategoryMeta={queryMeta}
                                    handleShowMoreCategories={async () => {}}
                                    setSelectElemList={(newSelectedItems) =>
                                        updateSelectedItems('products', newSelectedItems)
                                    }
                                    setSelectCatList={() => {}}
                                    openCategoryCard={null}
                                    openCard={openCard}
                                    infiniteScrollProps={{
                                        isActive: true,
                                        onNext: fetchItemsNextPage,
                                        totalItems: queryMeta?.total,
                                        itemsName: 'Товары',
                                        scrollableTarget: 'apelsin-app-content',
                                    }}
                                    isPermissions={true}
                                />
                            </SkeletonWrapper>
                        ),
                    },
                    {
                        label: 'Прочее',
                        key: '4',
                        children: (
                            <SkeletonWrapper
                                isLoading={isItemsListBusy}
                                skeleton={[1, 2, 3].map((id) => (
                                    <CardSkeleton key={id} />
                                ))}
                            >
                                <ServiceProductList
                                    productCategoryList={null}
                                    serviceCategoryList={[]}
                                    productList={itemsList}
                                    serviceList={null}
                                    selectElemList={selectedItems.products}
                                    selectCatList={selectedItems.product_categories}
                                    currentCategoryPage={1}
                                    currentCategoryMeta={queryMeta}
                                    handleShowMoreCategories={async () => {}}
                                    setSelectElemList={(newSelectedItems) =>
                                        updateSelectedItems('products', newSelectedItems)
                                    }
                                    setSelectCatList={() => {}}
                                    openCategoryCard={null}
                                    openCard={openCard}
                                    infiniteScrollProps={{
                                        isActive: true,
                                        onNext: fetchItemsNextPage,
                                        totalItems: queryMeta?.total,
                                        itemsName: 'Прочее',
                                        scrollableTarget: 'apelsin-app-content',
                                    }}
                                    isPermissions={true}
                                />
                            </SkeletonWrapper>
                        ),
                    },
                    {
                        label: 'Черновики',
                        key: '5',
                        children: (
                            <SkeletonWrapper
                                isLoading={isItemsListBusy}
                                skeleton={[1, 2, 3].map((id) => (
                                    <CardSkeleton key={id} />
                                ))}
                            >
                                <ServiceProductList
                                    productCategoryList={null}
                                    serviceCategoryList={[]}
                                    productList={itemsList}
                                    serviceList={null}
                                    selectElemList={selectedItems.products}
                                    selectCatList={selectedItems.product_categories}
                                    currentCategoryPage={1}
                                    currentCategoryMeta={queryMeta}
                                    handleShowMoreCategories={async () => {}}
                                    setSelectElemList={(newSelectedItems) =>
                                        updateSelectedItems('products', newSelectedItems)
                                    }
                                    setSelectCatList={() => {}}
                                    openCategoryCard={null}
                                    openCard={openCard}
                                    infiniteScrollProps={{
                                        isActive: true,
                                        onNext: fetchItemsNextPage,
                                        totalItems: queryMeta?.total,
                                        itemsName: 'Черновики',
                                        scrollableTarget: 'apelsin-app-content',
                                    }}
                                    isPermissions={true}
                                />
                            </SkeletonWrapper>
                        ),
                    },
                ]}
                onChange={(key) => {
                    if (key !== 'warehouses') {
                        handleChangeTab(key);
                    } else {
                        setTabNumber(key);
                    }
                }}
            />
        </div>
    );
});
